import { useRef, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

// Swipeable strip of one slab's photos: swipe/scroll on touch, arrows on hover, dots for position.
export const PhotoStrip = ({ photos, srcOf, onOpen, testPrefix, alt = "" }) => {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const count = photos.length;

  const goTo = (n, e) => {
    e?.stopPropagation();
    const el = ref.current;
    if (!el) return;
    const next = (n + count) % count;
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    setIdx(next);
  };
  const onScroll = () => {
    const el = ref.current;
    if (el?.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div className="group/strip relative h-full w-full" data-testid={`${testPrefix}-strip`}>
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {photos.map((p, n) => (
          <button
            key={p.path || n}
            type="button"
            data-testid={`${testPrefix}-shot-${n}`}
            onClick={() => onOpen?.(n)}
            title="Zoom this photo"
            className="h-full w-full shrink-0 snap-start cursor-zoom-in"
          >
            <img
              src={srcOf(p.thumb || p.path)}
              alt={alt}
              loading={n === 0 ? "eager" : "lazy"}
              className="h-full w-full object-cover"
            />
          </button>
        ))}
      </div>
      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            data-testid={`${testPrefix}-prev`}
            onClick={(e) => goTo(idx - 1, e)}
            className="absolute left-1 top-1/2 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-black shadow transition-opacity duration-150 group-hover/strip:flex"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <button
            type="button"
            aria-label="Next photo"
            data-testid={`${testPrefix}-next`}
            onClick={(e) => goTo(idx + 1, e)}
            className="absolute right-1 top-1/2 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-black shadow transition-opacity duration-150 group-hover/strip:flex"
          >
            <CaretRight size={14} weight="bold" />
          </button>
          <span
            data-testid={`${testPrefix}-count`}
            className="pointer-events-none absolute right-1.5 top-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white"
          >
            {idx + 1}/{count}
          </span>
          <div className="pointer-events-none absolute bottom-1.5 left-0 right-0 flex justify-center gap-1">
            {photos.map((_, n) => (
              <button
                key={n}
                type="button"
                aria-label={`Photo ${n + 1}`}
                data-testid={`${testPrefix}-dot-${n}`}
                onClick={(e) => goTo(n, e)}
                className={`pointer-events-auto h-1.5 rounded-full transition-all duration-200 ${
                  n === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoStrip;
